import React, { createContext, useContext, useEffect, ReactNode, useRef } from 'react';
import { useAuth } from './AuthProvider';
import useGetFullPersonREST from '../hooks/Person/useGetFullPersonREST';
import useGetEntitiesREST from '../hooks/Store/useGetEntitiesREST';
import type { UserDataResponse } from '../types/api';
import type { EntitiesResponse } from '../hooks/Store/useGetEntitiesREST';

interface UserDataContextType {
  personData: UserDataResponse | null;
  storeData: EntitiesResponse | null;
  loading: boolean;
  error: string | null;
  isDataLoaded: boolean;
  refetch: () => Promise<void>;
}

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

export const UserDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, keycloakInstance } = useAuth();
  const { fetchFullPerson, loading: loadingPerson, error: errorPerson, data: personData } = useGetFullPersonREST();
  const { fetchEntities, loading: loadingStore, error: errorStore, data: storeData } = useGetEntitiesREST();
  const hasInitializedRef = useRef(false);
  const identifierRef = useRef<string | null>(null);

  const loadData = async (identifierValue?: string) => {
    const idToUse = identifierValue || identifierRef.current || process.env.REACT_APP_DEFAULT_IDENTIFIER || '';
    
    const personResult = await fetchFullPerson({
      IdentifierRequest: {
        identifierType: 1,
        identifierValue: idToUse
      }
    });

    const identifiers = personResult?.PersonResponse?.IdentifierResponse || [];
    if (identifiers.length > 0) {
      identifierRef.current = identifiers[identifiers.length - 1].identifierValue;
    }

    if (personResult?.PersonResponse?.EntityResponse?.entityIdentifier) {
      await fetchEntities(personResult.PersonResponse.EntityResponse.entityIdentifier);
    }
  };

  useEffect(() => {
    if (isAuthenticated && keycloakInstance?.token && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      loadData();
    }
  }, [isAuthenticated, keycloakInstance?.token]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasInitializedRef.current = false;
    }
  }, [isAuthenticated]);

  return (
    <UserDataContext.Provider value={{
      personData,
      storeData,
      loading: loadingPerson || loadingStore,
      error: errorPerson || errorStore || null,
      isDataLoaded: !loadingPerson && !loadingStore && !!personData && !errorPerson && !errorStore,
      refetch: loadData,
    }}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = (): UserDataContextType => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};
